// src/prototype/components/EAInstrumentCard.tsx
// ============================================================
// ONE INSTRUMENT, WITH ITS SHAPE SHOWN BEFORE THE ASK
//
// ─────────────────────────────────────────────────────────────────────────────
// THE COMMITMENT GRADIENT IS THE POINT OF THIS COMPONENT.
//
//   1. column names are visible for free, with no interaction at all
//   2. a rendered slice of the completed ACME example is one click
//   3. the template download comes last, after the shape is understood
//
// Collapsing that into a download link is the failure mode. A client who has not seen the
// columns cannot tell whether the file is an afternoon or a week, so they do neither.
//
// EXPANDS INLINE. Not a drawer, not a modal. The design's 1b variant uses a drawer and
// this is 1a deliberately: a presenter should not have modal state to manage on camera,
// and an inline expansion cannot be left open behind another surface.
// ─────────────────────────────────────────────────────────────────────────────
//
// NO MEASURE NAME AND NO COUNT IS TYPED HERE. Names resolve through displayName(id) off
// the instrument's own measureIds; the count is that array's length.
// ============================================================

import { useState } from 'react';
import { ChevronDown, ChevronUp, Download, ExternalLink } from 'lucide-react';
import type { Instrument } from '../../data/coverage/gapInstruments';
import { displayName } from '../../data/coverage/measureDisplayNames';
import {
  INSTRUMENT_FRAGMENTS,
  extraRowCount,
  type InstrumentFragment,
} from '../../data/coverage/instrumentFragments';
import { TEMPLATE_ASSETS } from '../../data/coverage/templateAssets';

const measureCount = (n: number) => `${n} ${n === 1 ? 'measure' : 'measures'}`;
const extraRowsLine = (n: number, noun: string) =>
  `Plus ${n} more ${n === 1 ? noun : `${noun}s`} in the completed example.`;

/** Row noun per instrument, for the "plus N more" line. */
const ROW_NOUN: Record<string, string> = {
  D1: 'team row',
  D2: 'priority',
  D3: 'platform row',
  D4: 'control area',
};

export interface EAInstrumentCardProps {
  instrument: Instrument;
  defaultOpen?: boolean;
}

export function EAInstrumentCard({ instrument, defaultOpen = false }: EAInstrumentCardProps) {
  const [open, setOpen] = useState(defaultOpen);

  const key = instrument.id as keyof typeof INSTRUMENT_FRAGMENTS;
  const display = instrument.display;
  const fragment: InstrumentFragment | undefined = INSTRUMENT_FRAGMENTS[key];
  const assets = TEMPLATE_ASSETS[key];
  if (!display || !fragment || !assets) return null;

  const extra = extraRowCount(fragment);

  return (
    <div className="bg-white border border-[#e5e7eb] rounded-xl p-4 flex flex-col gap-2.5">
      {/* ---- header ---- */}
      <div className="flex items-center gap-2.5 flex-wrap">
        <span className="w-6 h-6 rounded-md bg-[#18365E] text-white text-[10px] font-bold flex items-center justify-center shrink-0">
          {display.code}
        </span>
        <span className="text-sm font-semibold text-[#18365E]">{instrument.label}</span>
        {display.flag && (
          <span
            className={`text-[10px] font-bold tracking-wide px-2 py-0.5 rounded-full whitespace-nowrap ${
              display.flag === 'Start here' || display.flag === 'Biggest gap'
                ? 'bg-[#FFF4F1] text-[#FF6E4C]'
                : 'bg-[#EEF2F7] text-[#18365E]'
            }`}
          >
            {display.flag}
          </span>
        )}
        <span className="ml-auto flex items-center gap-2">
          <span className="text-[11px] font-semibold text-[#18365E] bg-[#EEF2F7] rounded-full px-2.5 py-0.5 whitespace-nowrap">
            {/* Derived from the binding array. Never typed. */}
            {measureCount(instrument.measureIds.length)}
          </span>
          <span className="text-[11px] font-semibold text-[#717182] border border-[#e5e7eb] rounded-full px-2.5 py-0.5 whitespace-nowrap">
            {display.effortChip}
          </span>
        </span>
      </div>

      <p className="text-xs leading-relaxed text-[#717182]">{display.effect}</p>

      {/* ---- gradient step 1: the column names, free ---- */}
      <div className="flex flex-wrap gap-1.5">
        {fragment.columns.map((c) => (
          <span
            key={c.name}
            className="text-[10.5px] font-medium text-[#18365E] bg-[#F5F7FA] border border-[#e5e7eb] rounded px-2 py-0.5 whitespace-nowrap"
          >
            {c.name}
          </span>
        ))}
      </div>

      {/* ---- controls ---- */}
      <div className="flex items-center gap-4 flex-wrap">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="inline-flex items-center gap-1 text-xs font-bold text-[#FF6E4C] hover:underline"
        >
          {open ? 'Hide the shape' : 'See the shape'}
          {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </button>
        <a
          href={assets.templateHref ?? undefined}
          download={assets.templateFileName ?? undefined}
          className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[#18365E] hover:underline"
        >
          Blank template
          <Download className="w-3.5 h-3.5" />
        </a>
        <a
          href={assets.exampleHref}
          download={assets.exampleFileName}
          className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[#18365E] hover:underline"
        >
          Completed example
          <ExternalLink className="w-3.5 h-3.5" />
        </a>
        <span className="ml-auto text-[10.5px] text-[#9aa1ad]">{display.effortDetail}</span>
      </div>

      {/* ---- gradient step 2: the rendered slice, inline ---- */}
      {open && (
        <div className="bg-[#F5F7FA] rounded-lg p-3 flex flex-col gap-2.5">
          <div className="border border-[#e5e7eb] rounded-lg overflow-x-auto bg-white">
            <table className="w-full border-collapse text-left">
              <thead>
                <tr className="bg-[#18365E]">
                  {fragment.columns.map((c) => (
                    <th
                      key={c.name}
                      scope="col"
                      className="px-2.5 py-1.5 text-white text-[9.5px] font-semibold uppercase tracking-wider whitespace-nowrap"
                    >
                      {c.name}
                      {c.pin && (
                        <span className="inline-flex w-3.5 h-3.5 rounded-full bg-[#FF6E4C] text-white text-[8.5px] items-center justify-center ml-1 align-middle">
                          {c.pin}
                        </span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {fragment.rows.map((row, i) => (
                  <tr key={row[0] ?? i} className={i % 2 ? 'bg-[#FAFBFC]' : 'bg-white'}>
                    {row.map((cell, j) => (
                      <td
                        key={`${fragment.columns[j]?.name ?? j}`}
                        className="px-2.5 py-1.5 text-[10.5px] text-[#18365E] align-top max-w-[220px]"
                      >
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Only renders when the workbook holds more than the three shown. D1 has
              exactly three rows, so this is absent there rather than reading "plus 0". */}
          {extra > 0 && (
            <p className="text-[10.5px] text-[#9aa1ad]">
              {extraRowsLine(extra, ROW_NOUN[display.code] ?? 'row')}
            </p>
          )}

          <ul className="flex flex-col gap-1.5">
            {fragment.notes.map((note) => (
              <li key={note.n} className="flex gap-2 items-start">
                <span className="inline-flex w-3.5 h-3.5 mt-0.5 shrink-0 rounded-full bg-[#FF6E4C] text-white text-[8.5px] items-center justify-center">
                  {note.n}
                </span>
                <span className="text-[10.5px] leading-relaxed text-[#717182]">{note.t}</span>
              </li>
            ))}
          </ul>

          {/* ---- the before-and-after, in one glance ---- */}
          <div className="bg-white border border-[#e5e7eb] rounded-lg p-2.5">
            <p className="text-[9.5px] font-bold tracking-wider text-[#717182] mb-1">
              WHAT THESE ROWS PRODUCED
            </p>
            <p className="text-[11px] leading-relaxed text-[#18365E]">
              {fragment.derivedPre}
              <strong>{measureCount(instrument.measureIds.length)}</strong>
              {fragment.derivedPost}
            </p>
            <ul className="flex flex-wrap gap-1.5 mt-2">
              {instrument.measureIds.map((id) => (
                <li
                  key={id}
                  className="text-[10px] text-[#18365E] bg-[#F5F7FA] border border-[#e5e7eb] rounded px-1.5 py-0.5"
                >
                  {/* THE ONLY sanctioned accessor. No measure name is typed on this page. */}
                  {displayName(id)}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

export default EAInstrumentCard;
